import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Surface } from '@cloudflare/kumo/components/surface';
import { Banner } from '@cloudflare/kumo/components/banner';
import { Loader } from '@cloudflare/kumo/components/loader';
import { Badge } from '@cloudflare/kumo/components/badge';
import { Text } from '@cloudflare/kumo/components/text';
import { api } from '@/lib/api';
import YearSelector, { SupportedYear } from '@/components/YearSelector';
import { adminActionLabel, humanizeStatusTransition } from '@nt-tokyo/shared';

// 既存 API:
//   GET /api/admin/stats?year=YYYY    → 年度別件数集計
//   GET /api/admin/activities/recent  → admin_actions の直近 (年度横断)
//   GET /api/exhibitors/registration-status/:year → 受付モード / 残数

interface Stats {
  year: number;
  totalExhibitors: number;
  pendingReview: number;
  waitingForPayment: number;
  paidExhibitors: number;
  // キャンセル済みの件数。totalExhibitors には含まれない (実際に来る組数を出すため)。
  cancelled: number;
  totalRevenue: number;
  totalBooths: number;
  // 入金の進捗。金額は 2 種類あるので混ぜない:
  //   billedAmount / unpaidAmount = 請求ベース (現在の申込内容からの再計算値)
  //   paidAmount                  = 実入金 (payments の実額)
  payment: {
    billedCount: number;
    billedAmount: number;
    paidCount: number;
    paidAmount: number;
    unpaidCount: number;
    unpaidAmount: number;
  };
  // 収入の内訳 (実入金ベース)。少額スポンサーは出展料とは別制度なので分けて出す。
  income: {
    exhibitFee: number;
    sponsorAmount: number;
    sponsorUnits: number;
    sponsorSupporters: number;
    total: number;
  };
}

interface Activity {
  id: string;
  type: string;
  description: string;
  timestamp: string;
  exhibitorName: string | null;
}

type RegistrationOverride = 'auto' | 'force_open' | 'force_close';
type RegistrationPhase = 'tba' | 'upcoming' | 'open' | 'closed';
type RegistrationReason =
  | 'in_period' | 'before_start' | 'after_end' | 'limit_reached' | 'force_open' | 'force_close' | 'tba';

interface RegistrationStatus {
  year: number;
  phase: RegistrationPhase;
  reason: RegistrationReason;
  override: RegistrationOverride;
  currentCount: number;
  maxExhibitors: number | null;
}

export default function DashboardPage() {
  const [year, setYear] = useState<SupportedYear>(2026);
  const [stats, setStats] = useState<Stats | null>(null);
  const [regStatus, setRegStatus] = useState<RegistrationStatus | null>(null);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [pendingApprovalCount, setPendingApprovalCount] = useState<number>(0);
  const [pendingPaymentCount, setPendingPaymentCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        // 未送信件数は当年度のみ意味があるため year に依存しないが、表示の整合のため並列取得する。
        const [statsRes, regRes, actRes, approvalRes, paymentRes] = await Promise.all([
          api.get<{ data: Stats }>(`/api/admin/stats?year=${year}`),
          // ?live=1 でエッジキャッシュを迂回し、admin は常に最新の件数を見る。
          api.get<RegistrationStatus>(`/api/exhibitors/registration-status/${year}?live=1`),
          api.get<{ data: Activity[] }>('/api/admin/activities/recent?limit=20'),
          api.get<{ data: unknown[] }>('/api/admin/exhibitors/pending-approval-email'),
          api.get<{ data: unknown[] }>('/api/admin/exhibitors/pending-payment-notification'),
        ]);
        if (cancelled) return;
        setStats(statsRes.data.data ?? (statsRes.data as unknown as Stats));
        setRegStatus(regRes.data);
        setActivities(actRes.data.data ?? []);
        setPendingApprovalCount((approvalRes.data.data ?? []).length);
        setPendingPaymentCount((paymentRes.data.data ?? []).length);
      } catch (e) {
        if (cancelled) return;
        console.error(e);
        setError('統計の取得に失敗しました');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [year]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold text-kumo-default">ダッシュボード</h1>
        <YearSelector value={year} onChange={setYear} />
      </div>
      {error && <Banner variant="error">{error}</Banner>}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader />
        </div>
      ) : (
        <>
          {regStatus && <RegistrationStatusCard status={regStatus} />}

          {((stats?.pendingReview ?? 0) > 0 || pendingApprovalCount > 0 || pendingPaymentCount > 0) && (
            <Surface className="p-4">
              <h2 className="mb-3 text-base font-semibold text-kumo-default">未処理タスク</h2>
              <ul className="flex flex-col gap-2 text-sm">
                {(stats?.pendingReview ?? 0) > 0 && (
                  <li className="flex items-center justify-between">
                    <span>
                      内容確認 未処理
                      <span className="ml-2 font-mono font-semibold">{stats?.pendingReview ?? 0}</span> 件
                      <span className="ml-2 text-xs text-kumo-muted">(申込完了かつ未確認)</span>
                    </span>
                    <Link to="/content-review" className="text-xs text-kumo-link hover:underline">
                      内容確認へ →
                    </Link>
                  </li>
                )}
                {pendingApprovalCount > 0 && (
                  <li className="flex items-center justify-between">
                    <span>
                      内定通知メール 未送信
                      <span className="ml-2 font-mono font-semibold">{pendingApprovalCount}</span> 件
                      <span className="ml-2 text-xs text-kumo-muted">(内容確認済かつ未送信)</span>
                    </span>
                    <Link to="/emails" className="text-xs text-kumo-link hover:underline">
                      送信画面へ →
                    </Link>
                  </li>
                )}
                {pendingPaymentCount > 0 && (
                  <li className="flex items-center justify-between">
                    <span>
                      請求メール 未送信
                      <span className="ml-2 font-mono font-semibold">{pendingPaymentCount}</span> 件
                      <span className="ml-2 text-xs text-kumo-muted">(支払い待ちかつ未送信)</span>
                    </span>
                    <Link to="/payments" className="text-xs text-kumo-link hover:underline">
                      送信画面へ →
                    </Link>
                  </li>
                )}
              </ul>
            </Surface>
          )}

          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
            <StatCard label={`出展者総数 (${year}, キャンセル除く)`} value={stats?.totalExhibitors ?? 0} />
            <StatCard label="申込完了" value={stats?.pendingReview ?? 0} />
            <StatCard label="支払い待ち" value={stats?.waitingForPayment ?? 0} />
            <StatCard label="支払済" value={stats?.paidExhibitors ?? 0} />
            <StatCard label="キャンセル" value={stats?.cancelled ?? 0} />
            <StatCard label="ブース割当済" value={stats?.totalBooths ?? 0} />
          </div>

          {stats && <PaymentStatusCard payment={stats.payment} income={stats.income} />}

          <Surface className="p-4">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-base font-semibold text-kumo-default">直近の操作</h2>
              <Link to="/exhibitors" className="text-xs text-kumo-link hover:underline">
                出展者一覧へ
              </Link>
            </div>
            {activities.length === 0 ? (
              <Text>まだ記録がありません。</Text>
            ) : (
              <ul className="flex flex-col divide-y divide-kumo-line">
                {activities.map((a) => (
                  <li key={a.id} className="flex items-baseline gap-3 py-2 text-sm">
                    <span
                      className="w-36 shrink-0 text-xs text-kumo-muted"
                      title={formatJstAbsolute(a.timestamp)}
                    >
                      {formatRelative(a.timestamp)}
                    </span>
                    <span className="w-44 shrink-0 text-xs text-kumo-default">
                      {adminActionLabel(a.type)}
                    </span>
                    <span className="flex-1 truncate text-kumo-default">
                      {humanizeIsoUtcInText(humanizeStatusTransition(a.description))}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Surface>
        </>
      )}
    </div>
  );
}

function RegistrationStatusCard({ status }: { status: RegistrationStatus }) {
  const phaseBadge = renderPhaseBadge(status);
  const overrideBadge = renderOverrideBadge(status.override);
  const remaining =
    status.maxExhibitors === null ? null : Math.max(0, status.maxExhibitors - status.currentCount);
  const fillPercent =
    status.maxExhibitors === null || status.maxExhibitors === 0
      ? 0
      : Math.min(100, Math.round((status.currentCount / status.maxExhibitors) * 100));
  const barColor =
    fillPercent >= 100 ? 'bg-kumo-danger'
    : fillPercent >= 90 ? 'bg-kumo-warning'
    : 'bg-kumo-brand';

  return (
    <Surface className="p-4">
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-kumo-default">出展申込フォーム</span>
            {phaseBadge}
            {overrideBadge}
          </div>
          <Link to="/settings" className="text-xs text-kumo-link hover:underline">
            設定を変更
          </Link>
        </div>

        <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-6">
          <div className="text-sm text-kumo-default">
            申込数:{' '}
            <span className="font-mono text-base font-semibold">{status.currentCount}</span>
            {' / '}
            {status.maxExhibitors === null ? (
              <span className="text-kumo-muted">上限なし</span>
            ) : (
              <>
                <span className="font-mono">{status.maxExhibitors}</span> 組
                {remaining !== null && (
                  <span className="ml-2 text-kumo-muted">
                    (残り <span className="font-mono">{remaining}</span> 組)
                  </span>
                )}
              </>
            )}
          </div>
          {status.maxExhibitors !== null && (
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-kumo-tint">
              <div
                className={`h-full ${barColor} transition-all`}
                style={{ width: `${fillPercent}%` }}
              />
            </div>
          )}
        </div>

        <div className="text-xs text-kumo-muted">{renderReasonText(status)}</div>
      </div>
    </Surface>
  );
}

function renderPhaseBadge(status: RegistrationStatus) {
  if (status.phase === 'open') return <Badge variant="success">受付中</Badge>;
  if (status.phase === 'upcoming') return <Badge variant="info">受付前</Badge>;
  if (status.phase === 'closed') {
    if (status.reason === 'limit_reached') return <Badge variant="warning">受付終了 (上限到達)</Badge>;
    if (status.reason === 'force_close') return <Badge variant="error">強制停止中</Badge>;
    return <Badge variant="neutral">受付終了</Badge>;
  }
  return <Badge variant="neutral">期間未定</Badge>;
}

function renderOverrideBadge(override: RegistrationOverride) {
  if (override === 'force_open') return <Badge variant="warning">強制受付 ON</Badge>;
  if (override === 'force_close') return <Badge variant="error">強制停止 ON</Badge>;
  return <Badge variant="outline">自動</Badge>;
}

function renderReasonText(status: RegistrationStatus): string {
  switch (status.reason) {
    case 'in_period':
      return '受付期間中。期間内かつ上限未到達のため申込を受け付けています。';
    case 'before_start':
      return '受付開始前。開始日時になると自動で受付中になります。';
    case 'after_end':
      return '受付期間が終了しました。「強制受付」に切り替えると延長できます。';
    case 'limit_reached':
      return `募集上限 ${status.maxExhibitors ?? ''} 組に到達したため自動停止しています。上限を増やすか「強制受付」で受付を再開できます。`;
    case 'force_open':
      return '管理者が「強制受付」に設定中。期間・上限を無視して申込を受け付けています。';
    case 'force_close':
      return '管理者が「強制停止」に設定中。期間内・上限未到達でも申込を停止しています。';
    case 'tba':
      return '受付期間が未確定のため、フォームを未公開にしています。';
  }
}

const yen = (n: number) => `¥${(n ?? 0).toLocaleString('ja-JP')}`;

// 支払い状況。「請求したうち、どれだけ入金されたか」と「実際に入ってきたお金の内訳」を出す。
// 金額の基準が 2 つあるので、同じ行に混ぜず注記も添える:
//   請求ベース … 現在の申込内容からの再計算値 (まだ入っていないお金)
//   実入金     … payments の実額 (銀行振込等で payments 行が無い分は請求ベースで補完)
function PaymentStatusCard({
  payment,
  income,
}: {
  payment: Stats['payment'];
  income: Stats['income'];
}) {
  const percent =
    payment.billedCount > 0 ? Math.round((payment.paidCount / payment.billedCount) * 100) : 0;

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Surface className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold text-kumo-default">入金の進捗</h2>
          <Link to="/payments" className="text-xs text-kumo-link hover:underline">
            支払い管理へ →
          </Link>
        </div>

        <div className="mb-1 flex items-baseline justify-between">
          <span className="text-sm text-kumo-default">
            {payment.paidCount} / {payment.billedCount} 件
          </span>
          <span className="text-2xl font-semibold text-kumo-default">{percent}%</span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-kumo-tint">
          <div
            className="h-full rounded-full bg-kumo-brand"
            style={{ width: `${Math.min(100, percent)}%` }}
          />
        </div>

        <dl className="mt-4 flex flex-col gap-2 text-sm">
          <div className="flex items-baseline justify-between">
            <dt className="text-kumo-muted">請求済み</dt>
            <dd className="text-kumo-default">
              {payment.billedCount} 件
              <span className="ml-2 font-mono">{yen(payment.billedAmount)}</span>
            </dd>
          </div>
          <div className="flex items-baseline justify-between">
            <dt className="text-kumo-muted">入金済み</dt>
            <dd className="font-semibold text-kumo-default">
              {payment.paidCount} 件
              <span className="ml-2 font-mono">{yen(payment.paidAmount)}</span>
            </dd>
          </div>
          <div className="flex items-baseline justify-between">
            <dt className="text-kumo-muted">未入金</dt>
            <dd className="text-kumo-default">
              {payment.unpaidCount} 件
              <span className="ml-2 font-mono">{yen(payment.unpaidAmount)}</span>
            </dd>
          </div>
        </dl>

        <p className="mt-3 text-xs text-kumo-muted">
          ※ 「請求済み」「未入金」は現在の申込内容からの再計算値、「入金済み」は実際に決済された額です。
        </p>
      </Surface>

      <Surface className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold text-kumo-default">収入の内訳</h2>
          <Link to="/sponsorships" className="text-xs text-kumo-link hover:underline">
            少額スポンサーへ →
          </Link>
        </div>

        <dl className="flex flex-col gap-2 text-sm">
          <div className="flex items-baseline justify-between">
            <dt className="text-kumo-muted">出展料</dt>
            <dd className="font-mono text-kumo-default">{yen(income.exhibitFee)}</dd>
          </div>
          <div className="flex items-baseline justify-between">
            <dt className="text-kumo-muted">
              少額スポンサー
              <span className="ml-2 text-xs">
                ({income.sponsorUnits} 口 / {income.sponsorSupporters} 名)
              </span>
            </dt>
            <dd className="font-mono text-kumo-default">{yen(income.sponsorAmount)}</dd>
          </div>
          <div className="mt-1 flex items-baseline justify-between border-t border-kumo-line pt-2">
            <dt className="font-medium text-kumo-default">合計</dt>
            <dd className="font-mono text-lg font-semibold text-kumo-default">
              {yen(income.total)}
            </dd>
          </div>
        </dl>

        <p className="mt-3 text-xs text-kumo-muted">
          ※ 入金済みの実額です。出展申込と同時に申し込まれた少額スポンサーは、出展料と同じ決済に含まれるため出展料からは差し引いています。
        </p>
      </Surface>
    </div>
  );
}

function StatCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: number | string;
  hint?: string;
}) {
  return (
    <Surface className="p-4">
      <div className="text-xs text-kumo-muted">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-kumo-default">{value}</div>
      {hint && <div className="mt-1 text-xs text-kumo-muted">{hint}</div>}
    </Surface>
  );
}

function formatRelative(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return iso;
  const diff = Date.now() - t;
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'たった今';
  if (min < 60) return `${min} 分前`;
  const hour = Math.floor(min / 60);
  if (hour < 24) return `${hour} 時間前`;
  const day = Math.floor(hour / 24);
  if (day < 7) return `${day} 日前`;
  return new Date(iso).toLocaleDateString('ja-JP', { timeZone: 'Asia/Tokyo' });
}

const JST_DATETIME = new Intl.DateTimeFormat('ja-JP', {
  dateStyle: 'medium',
  timeStyle: 'short',
  timeZone: 'Asia/Tokyo',
});
function formatJstAbsolute(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return iso;
  return JST_DATETIME.format(new Date(iso));
}

// 操作ログ details 中の ISO UTC 文字列を JST (秒省略) に置換する。
// 例: '期限: 2026-05-23T14:59:59.000Z' → '期限: 2026-05-23 23:59 JST'
function humanizeIsoUtcInText(s: string): string {
  return s.replace(
    /(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2}):\d{2}(?:\.\d+)?Z/g,
    (_match, date: string, h: string, m: string) => {
      const iso = `${date}T${h}:${m}:00Z`;
      try {
        return JST_DATETIME.format(new Date(iso));
      } catch {
        return _match;
      }
    }
  );
}
